import { Head, router } from "@inertiajs/react"
import type { ColumnDef } from "@tanstack/react-table"
import { Pencil, Trash2 } from "lucide-react"

import TextLink from "@/components/text-link"
import { Badge } from "@/components/ui/badge"
import { DataTable } from "@/components/ui/data-table"
import AdminLayout from "@/layouts/admin-layout"
import {
  adminPartnerPath,
  adminPartnersPath,
  editAdminPartnerPath,
} from "@/routes"
import type { BreadcrumbItem, Pagy } from "@/types"

interface Partner {
  uid: string
  name: string
  state: string
  accounts_count: number
  created_at: string
}

const columns: ColumnDef<Partner>[] = [
  {
    accessorKey: "name",
    enableSorting: true,
    header: "Name",
    cell: ({ row }) => (
      <TextLink
        href={adminPartnerPath({ id: row.original.uid })}
        as="div"
        prefetch
      >
        {row.original.name}
      </TextLink>
    ),
  },
  {
    accessorKey: "accounts_count",
    header: "Accounts",
  },
  {
    accessorKey: "created_at",
    enableSorting: true,
    header: "Created",
    cell: ({ row }) =>
      new Date(row.original.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
  },
  {
    accessorKey: "state",
    enableSorting: true,
    header: "Status",
    cell: ({ row }) => (
      <Badge
        variant={row.original.state === "active" ? "operational" : "draft"}
        className="capitalize"
      >
        {row.original.state}
      </Badge>
    ),
  },
]

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Partners", href: adminPartnersPath() },
]

export default function PartnersIndex({
  partners,
  pagy,
  sort,
}: {
  partners: Partner[]
  pagy: Pagy
  sort: string | null
}) {
  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title="Partners" />
      <DataTable
        columns={columns}
        data={partners}
        pagy={pagy}
        sort={sort}
        onSortChange={(newSort) => {
          router.visit(adminPartnersPath(), {
            data: { sort: newSort },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        onPageChange={(page) => {
          router.visit(adminPartnersPath(), {
            data: { page, sort: sort ?? undefined },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        actions={[
          {
            label: "Edit",
            icon: <Pencil className="size-4" />,
            onClick: (partner: Partner) => {
              router.visit(editAdminPartnerPath({ id: partner.uid }))
            },
            variant: "default",
          },
          {
            label: "Delete",
            icon: <Trash2 className="size-4" />,
            onClick: (partner: Partner) => {
              router.delete(adminPartnerPath({ id: partner.uid }), {
                preserveState: true,
                preserveScroll: true,
              })
            },
            variant: "destructive",
          },
        ]}
      />
    </AdminLayout>
  )
}
